import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isViewerMode, type ViewerMode } from '../types';
import { useAdminStore } from './useAdminStore';

interface ViewerModeState {
  mode: ViewerMode;
  isPersonalAvailable: boolean;
  setMode: (mode: ViewerMode) => void;
  toggleMode: () => void;
  lockToFamily: () => void;
}

interface PersistedViewerModeState {
  mode: ViewerMode;
}

const DEFAULT_MODE: ViewerMode = 'personal';
const VIEWER_MODE_STORAGE_VERSION = 1;

const migrateViewerModeState = (persistedState: unknown): PersistedViewerModeState => {
  const persistedMode =
    typeof persistedState === 'object' && persistedState !== null && 'mode' in persistedState
      ? persistedState.mode
      : undefined;

  return { mode: isViewerMode(persistedMode) ? persistedMode : DEFAULT_MODE };
};

export const useViewerModeStore = create<ViewerModeState>()(
  persist(
    (set, get) => ({
      mode: DEFAULT_MODE,
      isPersonalAvailable: true,

      setMode: (mode: ViewerMode) => {
        if (mode === 'personal' && !get().isPersonalAvailable) {
          return;
        }

        if (mode === 'family') {
          // Family mode disables admin outright. Resetting the whole admin
          // store (not just the flag) also closes any open modal state, so
          // every isAdminMode read site goes dark from this one place.
          useAdminStore.setState(useAdminStore.getInitialState());
        }

        set({ mode });
      },

      toggleMode: () => {
        get().setMode(get().mode === 'personal' ? 'family' : 'personal');
      },

      // A family-only credential (the TV's device token) can never widen to personal,
      // so the toggle is pinned for the life of the page.
      lockToFamily: () => {
        get().setMode('family');
        set({ isPersonalAvailable: false });
      },
    }),
    {
      name: 'viewer-mode-storage',
      storage: createJSONStorage<PersistedViewerModeState>(() => localStorage),
      version: VIEWER_MODE_STORAGE_VERSION,
      partialize: ({ mode }) => ({ mode }),
      migrate: migrateViewerModeState,
    }
  )
);
